import React, { useState, useEffect } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  FaTachometerAlt,
  FaAd,
  FaVideo,
  FaGavel,
  FaMoneyBillWave,
  FaCreditCard,
  FaUserCircle,
  FaBars,
  FaWallet,
  FaTimes,
  FaLock,
  FaMicrophone,
  FaBullhorn,
  FaUsers,
  FaSignOutAlt,
  FaChartBar,
  FaSync,
  FaEye,
  FaArrowUp,
  FaArrowDown,
  FaCog,
  FaHome,
  FaGift,
  FaPlay,
  FaPause,
  FaStop,
  FaRocket
} from "react-icons/fa";
import InfluenerHeader from './InfluenerHeader';
import Swal from 'sweetalert2';

const InfluencerLayout = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeMobileMenu, setActiveMobileMenu] = useState(null); // 'ads', 'finance', null
  const [isProfileComplete, setIsProfileComplete] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const checkProfileCompletion = async () => {
      try {
        let user = null;
        try {
          const userString = localStorage.getItem('user');
          user = userString ? JSON.parse(userString) : null;
        } catch (e) {
          console.error('Error parsing user from localStorage:', e);
          user = null;
        }

        const isComplete = user && user.name && user.email && user.phone && user.address;
        setIsProfileComplete(isComplete);
      } catch (error) {
        console.error('Error checking profile completion:', error);
        setIsProfileComplete(false);
      }
    };

    checkProfileCompletion();
  }, []);

  const handleLinkClick = () => {
    if (window.innerWidth < 1024) {
      setIsSidebarOpen(false);
    }
  };

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to log out?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, log out",
    }).then((result) => {
      if (result.isConfirmed) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        navigate("/login");
        Swal.fire("Logged Out", "You have been logged out.", "success");
      }
    });
  };

  const menuItems = [
    {
      title: "🏠 Main",
      titleColor: "text-blue-600",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      items: [
        {
          path: "/influencer/dashboard",
          icon: FaTachometerAlt,
          label: "Dashboard",
          description: "Overview and analytics",
          gradient: "from-blue-500 to-blue-600",
          iconColor: "text-blue-500"
        }
      ]
    },
    {
      title: "📢 Content Management",
      titleColor: "text-purple-600",
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200",
      items: [
        {
          path: "/influencer/advertisement",
          icon: FaAd,
          label: "Paid Advertisements",
          description: "Manage paid ad campaigns",
          gradient: "from-purple-500 to-purple-600",
          iconColor: "text-purple-500"
        },
        {
          path: "/influencer/free-ads",
          icon: FaGift,
          label: "Reward ads",
          description: "Manage free ad campaigns",
          gradient: "from-green-500 to-emerald-600",
          iconColor: "text-green-500"
        },
        {
          path: "/influencer/raise-your-voice",
          icon: FaBullhorn,
          label: "Raise Your Voice",
          description: "Share your concerns and opinions",
          gradient: "from-yellow-500 to-orange-500",
          iconColor: "text-yellow-500"
        }
      ]
    },
    {
      title: "💰 Financial",
      titleColor: "text-teal-600",
      bgColor: "bg-teal-50",
      borderColor: "border-teal-200",
      items: [
        {
          path: "/influencer/wallet",
          icon: FaWallet,
          label: "Wallet",
          description: "Manage your earnings",
          gradient: "from-teal-500 to-cyan-600",
          iconColor: "text-teal-500"
        },
        {
          path: "/influencer/withdrawal",
          icon: FaMoneyBillWave,
          label: "Withdrawal Request",
          description: "Request withdrawals",
          gradient: "from-cyan-500 to-blue-600",
          iconColor: "text-cyan-500"
        }
      ]
    },

    {
      title: "⚙️ Account",
      titleColor: "text-slate-600",
      bgColor: "bg-slate-50",
      borderColor: "border-slate-200",
      items: [
        {
          path: "/influencer/id-card",
          icon: FaCreditCard,
          label: "View ID Card",
          description: "Your official credentials",
          gradient: "from-violet-500 to-purple-600",
          iconColor: "text-violet-500"
        },
        {
          path: "/influencer/id-cards",
          icon: FaCreditCard,
          label: "Identity Verification",
          description: "Apply for or update ID",
          gradient: "from-indigo-500 to-violet-600",
          iconColor: "text-indigo-500"
        },
        {
          path: "/influencer/changepassword",
          icon: FaLock,
          label: "Change Password",
          description: "Update your password",
          gradient: "from-gray-500 to-slate-600",
          iconColor: "text-gray-500"
        }
      ]
    }
  ];

  return (
    <div className="flex h-screen bg-[#F8FAFC] relative font-sans">
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #e2e8f0;
          border-radius: 10px;
        }
        @keyframes slide-up {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slide-up {
          animation: slide-up 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
      `}</style>

      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 transition-opacity duration-300"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar Toggle Button for Mobile */}
      {!isSidebarOpen && (
        <button
          className="lg:hidden fixed top-4 left-4 z-50 p-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl shadow-sm hover:shadow-md transition-all active:scale-95"
          onClick={() => setIsSidebarOpen(true)}
        >
          <FaBars className="w-5 h-5" />
        </button>
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed lg:static inset-y-0 left-0
          lg:w-[260px]
          w-72 bg-white border-r border-slate-100
          transform transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]
          ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}
          lg:translate-x-0 z-50 lg:z-auto
          flex flex-col shadow-[4px_0_24px_rgba(0,0,0,0.02)]
        `}
      >
        {/* Sidebar Header */}
        <div className="h-16 flex items-center justify-between px-4 sm:px-5 shrink-0 border-b border-slate-50">
          <div className="flex items-center gap-3 overflow-hidden">
            <div className="min-w-[40px] w-10 h-10 bg-purple-600 rounded-2xl flex items-center justify-center shadow-lg shadow-purple-200 group transition-transform hover:scale-105 active:scale-95 cursor-pointer" onClick={() => navigate('/influencer/dashboard')}>
              <FaRocket className="w-5 h-5 text-white" />
            </div>
            <div className="animate-fadeIn whitespace-nowrap">
              <h2 className="text-sm font-black text-slate-900 tracking-tight uppercase">Iinsaf</h2>
              <p className="text-[10px] font-bold text-purple-500 uppercase tracking-widest leading-none mt-0.5">Influencer</p>
            </div>
          </div>
          <button
            className="lg:hidden text-slate-400 hover:text-slate-600 p-2"
            onClick={() => setIsSidebarOpen(false)}
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation Section */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden pt-6 px-3 custom-scrollbar">
          {menuItems.map((section, sIdx) => (
            <div key={sIdx} className="mb-6">
              <div className="px-3 mb-2 flex items-center justify-between">
                <h3 className={`text-[10px] font-black uppercase tracking-[0.2em] ${section.titleColor} opacity-80`}>
                  {section.title}
                </h3>
              </div>
              <div className="space-y-1">
                {section.items.map((item, iIdx) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={iIdx}
                      to={item.path}
                      onClick={handleLinkClick}
                      className={`
                        group relative flex items-center gap-3 px-3 py-2 rounded-xl transition-all duration-300
                        ${isActive
                          ? `bg-purple-600 text-white shadow-lg shadow-purple-100`
                          : "text-slate-500 hover:text-purple-600 hover:bg-slate-50"
                        }
                      `}
                    >
                      <div className={`
                        shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-all
                        ${isActive ? "text-white" : `${item.iconColor} bg-slate-50 group-hover:bg-white`}
                      `}>
                        <item.icon className={`w-4 h-4 ${isActive ? 'drop-shadow-sm' : ''}`} />
                      </div>
                      <span className={`text-xs font-black tracking-tight flex-1 truncate transition-all ${isActive ? 'translate-x-1' : ''}`}>
                        {item.label}
                      </span>
                      {isActive && (
                        <div className="w-1.5 h-1.5 bg-white rounded-full shadow-sm animate-pulse" />
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Bottom Actions */}
        <div className="p-3 border-t border-slate-50">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-rose-50 hover:bg-rose-500 text-rose-600 hover:text-white transition-all duration-300 font-black text-xs uppercase tracking-widest shadow-sm active:scale-[0.98]"
          >
            <FaSignOutAlt className="w-4 h-4 shrink-0" />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        <InfluenerHeader
          isProfileComplete={isProfileComplete}
          onLogout={handleLogout}
        />
        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-slate-50/50 p-3 sm:p-5 lg:p-8 pb-24 lg:pb-8">
          <div className="max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Overlay for mobile bottom sub-menus */}
      {activeMobileMenu && !isSidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-slate-900/30 backdrop-blur-[2px] z-[55] transition-opacity duration-300"
          onClick={() => setActiveMobileMenu(null)}
        />
      )}

      {/* Mobile Professional Bottom Navigation */}
      <div className={`mobile-bottom-nav lg:hidden fixed bottom-0 left-0 right-0 z-[60] pointer-events-none transition-transform duration-300 ${isSidebarOpen ? 'translate-y-full' : 'translate-y-0'}`}>
        <div className="relative w-full bg-white border-t border-slate-100 pb-safe shadow-[0_-8px_30px_rgba(0,0,0,0.08)] pointer-events-auto z-50">

          {/* Sub-menu: Ads */}
          {activeMobileMenu === 'ads' && (
            <div className="absolute bottom-[100%] inset-x-0 mx-auto mb-5 w-[calc(100vw-60px)] max-w-[280px] bg-white/90 backdrop-blur-xl rounded-[2rem] shadow-[0_20px_50px_-12px_rgba(0,0,0,0.15)] border border-white/40 p-2 z-[70] flex gap-2 animate-slide-up origin-bottom">
              <button
                onClick={() => { navigate('/influencer/advertisement'); setActiveMobileMenu(null); }}
                className="flex-1 group relative flex flex-col items-center justify-center h-20 rounded-[1.5rem] bg-gradient-to-br from-purple-500 to-purple-700 text-white transition-all active:scale-95 shadow-md"
              >
                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center mb-1 group-hover:scale-110 transition-transform">
                  <FaAd className="w-5 h-5 text-white" />
                </div>
                <p className="text-[9px] font-black uppercase tracking-wider">Paid Ads</p>
              </button>
              <button
                onClick={() => { navigate('/influencer/free-ads'); setActiveMobileMenu(null); }}
                className="flex-1 group relative flex flex-col items-center justify-center h-20 rounded-[1.5rem] bg-gradient-to-br from-green-500 to-green-600 text-white transition-all active:scale-95 shadow-md"
              >
                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center mb-1 group-hover:scale-110 transition-transform">
                  <FaGift className="w-5 h-5 text-white" />
                </div>
                <p className="text-[9px] font-black uppercase tracking-wider">Rewards</p>
              </button>
            </div>
          )}

          {/* Sub-menu: Finance */}
          {activeMobileMenu === 'finance' && (
            <div className="absolute bottom-[100%] inset-x-0 mx-auto mb-5 w-[calc(100vw-60px)] max-w-[280px] bg-white/90 backdrop-blur-xl rounded-[2rem] shadow-[0_20px_50px_-12px_rgba(0,0,0,0.15)] border border-white/40 p-2 z-[70] flex gap-2 animate-slide-up origin-bottom">
              <button
                onClick={() => { navigate('/influencer/wallet'); setActiveMobileMenu(null); }}
                className="flex-1 group relative flex flex-col items-center justify-center h-20 rounded-[1.5rem] bg-gradient-to-br from-teal-500 to-cyan-600 text-white transition-all active:scale-95 shadow-md"
              >
                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center mb-1 group-hover:scale-110 transition-transform">
                  <FaWallet className="w-5 h-5 text-white" />
                </div>
                <p className="text-[9px] font-black uppercase tracking-wider">Wallet</p>
              </button>
              <button
                onClick={() => { navigate('/influencer/withdrawal'); setActiveMobileMenu(null); }}
                className="flex-1 group relative flex flex-col items-center justify-center h-20 rounded-[1.5rem] bg-gradient-to-br from-sky-500 to-blue-600 text-white transition-all active:scale-95 shadow-md"
              >
                <div className="w-9 h-9 rounded-xl bg-white/20 flex items-center justify-center mb-1 group-hover:scale-110 transition-transform">
                  <FaMoneyBillWave className="w-5 h-5 text-white" />
                </div>
                <p className="text-[9px] font-black uppercase tracking-wider">Payout</p>
              </button>
            </div>
          )}

          {/* Bottom Bar Icons */}
          <div className="flex items-center justify-between h-[72px] px-2 sm:px-6">
            <div className="flex-1 flex justify-around">
              <button
                onClick={() => { navigate('/influencer/dashboard'); setActiveMobileMenu(null); }}
                className={`flex flex-col items-center justify-center gap-1 w-12 h-12 transition-all active:scale-95 ${location.pathname.includes('dashboard') ? 'text-purple-600' : 'text-slate-400'}`}
              >
                <FaTachometerAlt className="w-5 h-5" />
                <span className="text-[8px] font-black uppercase tracking-tighter">Home</span>
              </button>

              <button
                onClick={() => setActiveMobileMenu(activeMenu => activeMenu === 'ads' ? null : 'ads')}
                className={`flex flex-col items-center justify-center gap-1 w-12 h-12 transition-all active:scale-95 ${activeMobileMenu === 'ads' ? 'text-purple-600 scale-110' : 'text-slate-400'}`}
              >
                <div className="relative">
                  <FaAd className="w-5 h-5" />
                  {activeMobileMenu === 'ads' && <div className="absolute -top-1 -right-1 w-2 h-2 bg-purple-500 rounded-full ring-2 ring-white" />}
                </div>
                <span className="text-[8px] font-black uppercase tracking-tighter">Ads</span>
              </button>
            </div>

            {/* Center FAB: Raise Voice */}
            <div className="flex flex-col items-center justify-end h-full pb-1.5 px-2 min-w-[80px]">
              <div className="relative -top-3">
                <button
                  onClick={() => { navigate('/influencer/raise-your-voice'); setActiveMobileMenu(null); }}
                  className={`w-14 h-14 bg-gradient-to-tr from-purple-600 to-pink-600 rounded-2xl flex items-center justify-center text-white shadow-xl shadow-purple-200 border-4 border-white transition-all active:scale-95 ${location.pathname.includes('raise-your-voice') ? "scale-105 shadow-purple-400" : ""}`}
                >
                  <FaBullhorn className="w-6 h-6" />
                </button>
              </div>
              <span className={`text-[8px] font-black uppercase tracking-tighter leading-none px-2 py-1 rounded-full border transition-all ${location.pathname.includes('raise-your-voice') ? 'bg-purple-600 text-white border-purple-600 shadow-md' : 'bg-white text-purple-600 border-slate-100 shadow-sm'}`}>
                Voice
              </span>
            </div>

            <div className="flex-1 flex justify-around">
              <button
                onClick={() => setActiveMobileMenu(activeMenu => activeMenu === 'finance' ? null : 'finance')}
                className={`flex flex-col items-center justify-center gap-1 w-12 h-12 transition-all active:scale-95 ${activeMobileMenu === 'finance' ? 'text-purple-600 scale-110' : 'text-slate-400'}`}
              >
                <div className="relative">
                  <FaWallet className="w-5 h-5" />
                  {activeMobileMenu === 'finance' && <div className="absolute -top-1 -right-1 w-2 h-2 bg-purple-500 rounded-full ring-2 ring-white" />}
                </div>
                <span className="text-[8px] font-black uppercase tracking-tighter">Finance</span>
              </button>

              <button
                onClick={() => { navigate('/influencer/id-card'); setActiveMobileMenu(null); }}
                className={`flex flex-col items-center justify-center gap-1 w-12 h-12 transition-all active:scale-95 ${location.pathname.includes('id-card') ? 'text-purple-600' : 'text-slate-400'}`}
              >
                <FaCreditCard className="w-5 h-5" />
                <span className="text-[8px] font-black uppercase tracking-tighter">ID Card</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfluencerLayout;
